import { AgentAdapter } from '@tylertech/forge-ai';
import {
  STREAM_INACTIVITY_TIMEOUT_MS,
  DEFAULT_CHATBOT_ERROR_MESSAGE,
  buildPayload,
  injectUserQuestion,
  updateTextTurnQueue,
  getNextTypingStep,
  createSseLineProcessor,
  processSseStream,
} from './pbbStrategistAdapterHelpers.js';
import { createRandomID } from '../../reusable/reusableFunctions.js';

export class PbbStrategistAdapter extends AgentAdapter {
  constructor() {
    super();

    this._threadId = createRandomID();
    this.currentPrompt = null;
    this.abortController = null;
    this.inactivityTimeout = null;
    this.typingInterval = null;
    this.activeMessage = null;
    this.runFinished = false;
    this.streamCompleted = false;
    this.turnQueue = [];
    this.chatbotEl = null;
    this.messageIdAliases = new Map();
  }

  // This is the getter for retrieving the current thread ID.
  get threadId() {
    return this._threadId;
  }

  // This is the setter for updating the current thread ID.
  set threadId(value) {
    this._threadId = value;
  }

  setChatbotElement(chatbotEl) {
    this.chatbotEl = chatbotEl;
  }

  setMessageIdAlias(oldId, newId) {
    if (!oldId || !newId || oldId === newId) return;
    this.messageIdAliases.set(oldId, newId);
  }

  resolveMessageId(id) {
    if (!id) return id;

    let resolved = id;
    const visited = new Set();

    // Follow alias chains safely in case an ID is remapped more than once.
    while (this.messageIdAliases.has(resolved) && !visited.has(resolved)) {
      visited.add(resolved);
      resolved = this.messageIdAliases.get(resolved);
    }

    return resolved;
  }

  // This is for setting the current prompt type and context before sending a message.
  setPromptContext(type, context) {
    this.currentPrompt = { type, context };
  }

  // This is for clearing the stored prompt context after it has been used.
  clearPromptContext() {
    this.currentPrompt = null;
  }

  // This is for setting the adapter connection state to active.
  async connect() {
    this._updateState({ isConnected: true });
  }

  // This is for disconnecting the adapter, aborting running streams, and updating connection state.
  async disconnect() {
    this.abort();
    this._updateState({ isConnected: false });
  }

  // This is for clearing the active inactivity timeout timer.
  _clearInactivityTimeout() {
    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
      this.inactivityTimeout = null;
    }
  }

  // This is for resetting the stream inactivity timer to catch stalled responses.
  _resetInactivityTimeout() {
    this._clearInactivityTimeout();
    this.inactivityTimeout = setTimeout(() => {
      this._handleStreamError();
    }, STREAM_INACTIVITY_TIMEOUT_MS);
  }

  // This is for clearing the typing interval timer.
  _clearTypingInterval() {
    if (this.typingInterval) {
      clearInterval(this.typingInterval);
      this.typingInterval = null;
    }
  }

  // This is for running the typing animation loop that processes queued messages character by character.
  _startTypingLoop() {
    if (this.typingInterval) return;

    const INTERVAL_MS = 20;

    this.typingInterval = setInterval(() => {
      if (this.turnQueue.length === 0) {
        if (this.streamCompleted) {
          this._finishAssistantMessage();
          this._clearTypingInterval();
          this._finalizeSuccess();
        }
        return;
      }

      const currentTurn = this.turnQueue[0];

      if (currentTurn.type === 'question') {
        this._finishAssistantMessage();
        this._emitRunFinished();

        this.turnQueue.shift();

        const userMsgId = `user-msg-${createRandomID()}`;
        this._emitMessageStart(userMsgId, {
          id: userMsgId,
          role: 'user',
          content: currentTurn.text,
          timestamp: Date.now(),
        });

        this._emitMessageEnd(userMsgId, {
          id: userMsgId,
          role: 'user',
          content: currentTurn.text,
          status: 'complete',
          timestamp: Date.now(),
        });

        injectUserQuestion(currentTurn.text);

        this._emitRunStarted();
        this._updateState({ isRunning: true });

        return;
      }

      if (currentTurn.type === 'text') {
        this._startAssistantMessage();

        const { delta, updatedTypedText, isFinished } = getNextTypingStep(currentTurn);

        if (delta) {
          currentTurn.typedText = updatedTypedText;
          this.activeMessage.typedText = currentTurn.typedText;
          this._emitMessageDelta(this.activeMessage.id, delta);
        }

        if (isFinished && (this.turnQueue.length > 1 || this.streamCompleted)) {
          this._finishAssistantMessage();
          this.turnQueue.shift();
        }
      }
    }, INTERVAL_MS);
  }

  // This is for adding a user question turn to the queue and starting the output loop.
  _pushQuestion(questionText) {
    if (!questionText) return;
    this.turnQueue.push({ type: 'question', text: questionText });
    this._startTypingLoop();
  }

  // This is for updating the text turn queue with new assistant output chunks.
  _updateAssistantText(normalizedOutput) {
    updateTextTurnQueue(this.turnQueue, normalizedOutput);
    this._startTypingLoop();
  }

  // This is for initializing a new assistant message bubble and emitting the messageStart event.
  _startAssistantMessage() {
    if (this.activeMessage) return;

    const id = `msg-${createRandomID()}`;
    this.activeMessage = { id, typedText: '' };
    this._emitMessageStart(id, {
      id,
      role: 'assistant',
      status: 'streaming',
      timestamp: Date.now(),
      allowReactions: true,
      showActions: true,
    });
  }

  // This is for marking the active assistant message as completed and emitting the messageEnd event.
  _finishAssistantMessage() {
    if (!this.activeMessage) return;

    const messageId = this.activeMessage.id;
    const content = this.activeMessage.typedText;
    this._emitMessageEnd(messageId, {
      id: messageId,
      role: 'assistant',
      status: 'complete',
      content: content,
      timestamp: Date.now(),
      allowReactions: true,
      showActions: true,
      isComplete: true,
    });

    this.activeMessage = null;
  }

  // This is the main method for executing the API fetch call and handling incoming SSE streams.
  async sendMessage(messages) {
    this._resetStreamState();

    this._emitRunStarted();
    this._updateState({ isRunning: true });

    const payload = buildPayload(messages, this.threadId, this.currentPrompt);

    this.clearPromptContext();
    this._resetInactivityTimeout();

    try {
      const response = await fetch('/api/chatbot/query', {
        method: 'POST',
        headers: {
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: this.abortController?.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const processLine = createSseLineProcessor({
        onResetTimeout: () => this._resetInactivityTimeout(),
        onQuestion: (question) => this._pushQuestion(question),
        onMessage: (text) => this._updateAssistantText(text),
      });

      await processSseStream(response, processLine);

      this._clearInactivityTimeout();
      this.streamCompleted = true;
      this._startTypingLoop();
    } catch (error) {
      if (error.name !== 'AbortError') {
        this._handleStreamError();
      }
    }
  }

  // This is for resetting internal stream state and abort controllers before starting a new run.
  _resetStreamState() {
    this.runFinished = false;
    this.streamCompleted = false;
    this.turnQueue = [];
    this.activeMessage = null;
    this.abortController = new AbortController();
  }

  // This is for completing the run successfully and emitting the runFinished event.
  _finalizeSuccess() {
    if (this.runFinished) return;

    this.runFinished = true;
    this.turnQueue = [];
    this._clearTypingInterval();
    this._clearInactivityTimeout();
    this._updateState({ isRunning: false });

    queueMicrotask(() => this._emitRunFinished());
  }

  // This is for handling stream errors, resetting timers, and emitting error and abort events.
  _handleStreamError() {
    this.streamCompleted = true;
    this.turnQueue = [];
    this._clearTypingInterval();
    this._clearInactivityTimeout();

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this._finishAssistantMessage();
    this._emitError(DEFAULT_CHATBOT_ERROR_MESSAGE);
    this._emitRunAborted();
    this._updateState({ isRunning: false });
  }

  // This is an empty stub for tool result handling since tools are not used in this adapter.
  sendToolResult() { }

  // This is for manually aborting the active stream run and stopping all typing timers.
  abort() {
    this.streamCompleted = true;
    this.turnQueue = [];

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this._clearTypingInterval();
    this._clearInactivityTimeout();
    this._finishAssistantMessage();
    this._emitRunAborted();
    this._updateState({ isRunning: false });
  }
}
